import { Utensils } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import CompanyDescription from '../company/CompanyDescription';
import TableName from './TableName';
import { ORDER_FULFILLMENT_STATUSES } from '../../services/exports/Constants';
import { Order } from '../../services/exports/Interfaces';
import { getOrderTableName } from '../../utils/on-site';

interface Props {
  order: Order;
}

/**
 * "Your table" on the order screen of a table order (PROD-724 criterion 18): the table, the restaurant,
 * and, while the order is still on its way, what to do after moving tables.
 */
export default function TableDetailsCard({ order }: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:OrderType:TableOrdering' });
  const tableName = getOrderTableName(order);
  // Moving tables matters while the order is still on its way; a served (ready, as OrderTracker reads
  // it for a table order, or picked) or rejected one is over.
  const isOngoing = ![
    ORDER_FULFILLMENT_STATUSES.READY,
    ORDER_FULFILLMENT_STATUSES.PICKED,
    ORDER_FULFILLMENT_STATUSES.REJECTED,
  ].includes(order?.fulfillment?.status);

  return (
    <div className={sectionCard} data-test="order-details-your-table">
      <div className="flex items-center gap-2xs mb-sm">
        <Utensils className="w-[18px] h-[18px] text-brand-primary shrink-0" />
        <p className="text-sm font-bold text-brand-text-primary">{t('confirmation.your_table')}</p>
      </div>
      {tableName && <TableName name={tableName} className="block text-base mb-xs" />}
      <CompanyDescription company={order.company} shouldOpenMap />
      {isOngoing && (
        <div className={movedTable} data-test="order-details-moved-table">
          <Trans
            t={t}
            values={{ number: order?.fulfillment?.number ?? order?.id }}
            components={{ strong: <strong className="text-brand-text-primary" /> }}
          >
            confirmation.moved_table
          </Trans>
        </div>
      )}
    </div>
  );
}

const sectionCard = 'rounded-xl border-xs border-brand-border bg-brand-card p-md';
const movedTable = 'bg-muted/50 rounded-lg px-sm py-xs text-sm text-muted-foreground mt-md';
